import React, { useEffect } from "react";
import { Box, IconButton, Modal, Stack, Typography, alpha } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTranslation } from "react-i18next";
import { attachmentKind } from "../utils/taskAttachmentKind";

const TaskAttachmentLightbox = ({ attachments = [], index, onClose, onIndexChange }) => {
  const { t } = useTranslation("teamTasks");
  const open = index != null && index >= 0 && Boolean(attachments[index]);
  const current = open ? attachments[index] : null;
  const kind = current ? attachmentKind(current) : "file";
  const hasPrev = index > 0;
  const hasNext = index != null && index < attachments.length - 1;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "ArrowLeft" && hasPrev) onIndexChange(index - 1);
      if (event.key === "ArrowRight" && hasNext) onIndexChange(index + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, hasPrev, hasNext, index, onIndexChange]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-label={t("attachmentPreviewAria")}
      sx={{
        zIndex: 20000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 1.5, sm: 2 },
      }}
      slotProps={{ backdrop: { sx: { bgcolor: alpha("#000", 0.88) } } }}
    >
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
          outline: "none",
        }}
        onClick={onClose}
      >
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label={t("attachmentPreviewClose")}
          sx={{
            position: "fixed",
            top: 12,
            right: 12,
            zIndex: 1,
            color: "common.white",
            bgcolor: alpha("#000", 0.45),
            "&:hover": { bgcolor: alpha("#000", 0.65) },
          }}
        >
          <CloseIcon />
        </IconButton>

        {current?.fileUrl ? (
          <IconButton
            component="a"
            href={current.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            aria-label={t("attachmentOpenOriginal")}
            sx={{
              position: "fixed",
              top: 12,
              right: 60,
              zIndex: 1,
              color: "common.white",
              bgcolor: alpha("#000", 0.45),
              "&:hover": { bgcolor: alpha("#000", 0.65) },
            }}
          >
            <OpenInNewIcon />
          </IconButton>
        ) : null}

        {hasPrev ? (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onIndexChange(index - 1);
            }}
            aria-label={t("attachmentPreviewPrev")}
            sx={{
              position: "fixed",
              left: 8,
              top: "50%",
              zIndex: 1,
              color: "common.white",
              bgcolor: alpha("#000", 0.45),
              "&:hover": { bgcolor: alpha("#000", 0.65) },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        ) : null}

        {hasNext ? (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onIndexChange(index + 1);
            }}
            aria-label={t("attachmentPreviewNext")}
            sx={{
              position: "fixed",
              right: 8,
              top: "50%",
              zIndex: 1,
              color: "common.white",
              bgcolor: alpha("#000", 0.45),
              "&:hover": { bgcolor: alpha("#000", 0.65) },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        ) : null}

        {current ? (
          <Stack
            spacing={1}
            alignItems="center"
            sx={{ maxWidth: "100%", maxHeight: "100%" }}
            onClick={(e) => e.stopPropagation()}
          >
            {kind === "image" ? (
              <Box
                component="img"
                src={current.fileUrl}
                alt={current.fileName || t("attachmentPreviewAria")}
                sx={{
                  maxWidth: "calc(100dvw - 48px)",
                  maxHeight: "calc(100dvh - 88px)",
                  objectFit: "contain",
                  borderRadius: 1,
                  boxShadow: 8,
                  userSelect: "none",
                }}
              />
            ) : null}
            {kind === "video" ? (
              <Box
                component="video"
                src={current.fileUrl}
                controls
                autoPlay
                sx={{
                  maxWidth: "calc(100dvw - 48px)",
                  maxHeight: "calc(100dvh - 88px)",
                  borderRadius: 1,
                  bgcolor: "#000",
                }}
              />
            ) : null}
            {kind === "pdf" ? (
              <Box
                component="iframe"
                title={current.fileName || t("attachmentPreviewAria")}
                src={current.fileUrl}
                sx={{
                  width: "min(960px, calc(100dvw - 48px))",
                  height: "calc(100dvh - 88px)",
                  border: 0,
                  borderRadius: 1,
                  bgcolor: "background.paper",
                }}
              />
            ) : null}
            {current.fileName ? (
              <Typography variant="caption" sx={{ color: "common.white" }} noWrap>
                {current.fileName}
              </Typography>
            ) : null}
          </Stack>
        ) : null}
      </Box>
    </Modal>
  );
};

export default TaskAttachmentLightbox;
